/**
 * Quadro de Avisos (Portal do Condômino, 0.1.31). Mobile-first.
 * O condômino lê e confirma ciência; quem tem permissão de escrever publica/exclui.
 * 0.1.90: "Para quem" — todo o condomínio ou blocos/torres, e todos / só proprietários / só
 * quem mora (o servidor filtra quem recebe e quem vê no Portal).
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Check, Loader2, Megaphone, Pin, Plus, Trash2 } from "lucide-react";
import { api, ApiError } from "../lib/api";
import { PUBLICO_TODOS, PublicoSeletor, publicoParaEnvio, type Publico } from "./PublicoSeletor";

interface Aviso {
  id: number;
  titulo: string;
  corpo: string;
  categoria: string;
  fixado: boolean;
  autor: string | null;
  publicado_em: string | null;
  expira_em: string | null;
  lido: boolean;
  publico_desc?: string;
}
interface AvisosResp {
  condominio_id: number;
  itens: Aviso[];
  categorias: string[];
  pode_publicar: boolean;
}

const fmt = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "";

export function AvisosPage({
  condominioId, onVoltar, gestao = true,
}: {
  condominioId: number;
  onVoltar: () => void;
  gestao?: boolean;   // false = aberto pelo Portal (só consulta, mesmo p/ síndico)
}) {
  const qc = useQueryClient();
  const inval = () => qc.invalidateQueries({ queryKey: ["avisos", condominioId] });
  const { data, isLoading } = useQuery<AvisosResp>({
    queryKey: ["avisos", condominioId],
    queryFn: () => api.get(`/condominios/${condominioId}/avisos`),
  });
  // No Portal a tela é só de consulta; a publicação fica no menu inicial do condomínio.
  const podePublicar = gestao && !!data?.pode_publicar;

  const [novo, setNovo] = useState(false);
  const [titulo, setTitulo] = useState("");
  const [corpo, setCorpo] = useState("");
  const [categoria, setCategoria] = useState("Geral");
  const [fixado, setFixado] = useState(false);
  const [expira, setExpira] = useState("");
  const [publico, setPublico] = useState<Publico>(PUBLICO_TODOS);
  const [erro, setErro] = useState<string | null>(null);

  const publicar = useMutation({
    mutationFn: () =>
      api.post(`/condominios/${condominioId}/avisos`, {
        titulo, corpo, categoria, fixado, expira_em: expira || null,
        publico: publicoParaEnvio(publico),
      }),
    onSuccess: () => {
      setNovo(false); setTitulo(""); setCorpo(""); setCategoria("Geral");
      setFixado(false); setExpira(""); setPublico(PUBLICO_TODOS); setErro(null); inval();
    },
    onError: (e) => setErro(e instanceof ApiError ? e.message : "Não foi possível publicar."),
  });
  const ciencia = useMutation({
    mutationFn: (id: number) => api.post(`/avisos/${id}/ciencia`, {}),
    onSuccess: () => inval(),
  });
  const excluir = useMutation({
    mutationFn: (id: number) => api.delete(`/avisos/${id}`),
    onSuccess: () => inval(),
  });

  const itens = data?.itens ?? [];

  return (
    <div className="max-w-2xl mx-auto">
      <button onClick={onVoltar} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-4">
        <ArrowLeft size={16} /> Voltar
      </button>
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-brand-700 text-white flex items-center justify-center"><Megaphone size={20} /></div>
          <h1 className="text-2xl font-bold text-slate-900">Quadro de Avisos</h1>
        </div>
        {podePublicar && !novo && (
          <button onClick={() => { setNovo(true); setErro(null); }}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-700 text-white px-3 py-2 text-sm font-medium hover:bg-brand-800">
            <Plus size={16} /> Novo aviso
          </button>
        )}
      </div>

      {novo && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 space-y-2">
          <input placeholder="Título" value={titulo} onChange={(e) => setTitulo(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          <textarea placeholder="Escreva o comunicado…" value={corpo} onChange={(e) => setCorpo(e.target.value)}
            rows={4} wrap="soft"
            style={{ whiteSpace: "pre-wrap", overflowWrap: "break-word", wordBreak: "break-word" }}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm resize-y" />
          <div className="flex flex-wrap items-center gap-3">
            <select value={categoria} onChange={(e) => setCategoria(e.target.value)}
              className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm bg-white">
              {(data?.categorias ?? ["Geral"]).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <label className="flex items-center gap-1.5 text-sm text-slate-700">
              <input type="checkbox" checked={fixado} onChange={(e) => setFixado(e.target.checked)} /> Fixar no topo
            </label>
            <label className="flex items-center gap-1.5 text-sm text-slate-700">
              Validade: <input type="date" value={expira} onChange={(e) => setExpira(e.target.value)}
                className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
            </label>
          </div>
          <PublicoSeletor condominioId={condominioId} valor={publico} onChange={setPublico} />
          {erro && <div className="text-xs text-rose-700">{erro}</div>}
          <div className="flex justify-end gap-2">
            <button onClick={() => setNovo(false)} className="px-3 py-2 text-sm text-slate-600">Cancelar</button>
            <button onClick={() => publicar.mutate()} disabled={!titulo || !corpo || publicar.isPending}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-700 text-white px-4 py-2 text-sm font-semibold disabled:opacity-40">
              {publicar.isPending ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Publicar
            </button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm py-10 justify-center">
          <Loader2 size={16} className="animate-spin" /> Carregando…
        </div>
      ) : itens.length === 0 ? (
        <div className="text-center text-sm text-slate-500 bg-white border border-slate-200 rounded-xl p-8">
          Nenhum aviso publicado ainda.
        </div>
      ) : (
        <div className="space-y-3">
          {itens.map((a) => (
            <div key={a.id} className={`bg-white border rounded-xl p-4 ${a.fixado ? "border-amber-300" : "border-slate-200"}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  {a.fixado && <Pin size={15} className="text-amber-600" />}
                  <h3 className="font-semibold text-slate-900">{a.titulo}</h3>
                  <span className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 text-[11px]">{a.categoria}</span>
                  {a.publico_desc && (
                    <span className="px-2 py-0.5 rounded-md bg-teal-50 text-teal-700 border border-teal-200 text-[11px]" title="Para quem é este aviso">
                      {a.publico_desc}
                    </span>
                  )}
                </div>
                {podePublicar && (
                  <button onClick={() => excluir.mutate(a.id)} title="Excluir"
                    className="text-slate-400 hover:text-rose-600"><Trash2 size={16} /></button>
                )}
              </div>
              <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{a.corpo}</p>
              <div className="mt-2 flex items-center justify-between gap-2">
                <span className="text-[11px] text-slate-400">
                  {a.autor ? `${a.autor} · ` : ""}{fmt(a.publicado_em)}
                  {a.expira_em ? ` · vale até ${fmt(a.expira_em)}` : ""}
                </span>
                {a.lido ? (
                  <span className="inline-flex items-center gap-1 text-[12px] text-emerald-700"><Check size={14} /> Ciente</span>
                ) : (
                  <button onClick={() => ciencia.mutate(a.id)} disabled={ciencia.isPending}
                    className="inline-flex items-center gap-1 rounded-md border border-emerald-300 text-emerald-700 px-2.5 py-1 text-[12px] font-medium hover:bg-emerald-50">
                    <Check size={14} /> Confirmar ciência
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
